import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { DateTime } from "luxon";
import SwiperCore, { Navigation, A11y } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import isEqual from "react-fast-compare";

import IconArrow from "../../../components/icons/arrow";
import { changeMobileClasse } from "../../../redux/actions/options";
import { loadLessons } from "../../../redux/actions/lessons";
import { getLessons } from "../../../redux/selectors/lessons";
import Lesson from "./Lesson";

SwiperCore.use([Navigation, A11y]);

class Calendar extends Component {
    constructor(props) {
        super(props);

        let now = DateTime.local();

        if (this.props.selectedDate) {
            let date = DateTime.fromISO(this.props.selectedDate);

            this.state = {
                selectedMonth: date.month,
                selectedYear: date.year,
                selectedDay: date.day,
            };
        } else {
            this.state = {
                selectedMonth: now.month,
                selectedYear: now.year,
                selectedDay: null,
            };
        }

        this.begin = now.startOf("month").minus({ month: 2 });
        this.end = now.endOf("month").plus({ month: 2 });

        this._loadLessons = this._loadLessons.bind(this);

        this._prevArrow = null;
        this._nextArrow = null;
    }

    componentDidMount() {
        this._loadLessons();

        if (this.props.lessons.length > 0 && !this.props.selectedDate) {
            let lesson = this.props.lessons.find((l) => {
                let lDate = DateTime.fromISO(l.date);

                return Math.round(lDate.diffNow("days").days) >= 0;
            });

            if (!lesson) {
                lesson = this.props.lessons[0];
            }

            let date = DateTime.fromISO(lesson.date).toUTC();
            this.setState({
                selectedYear: date.year,
                selectedMonth: date.month,
                selectedDay: date.day,
            });
        }
    }

    componentDidUpdate(prevProps) {
        if (
            !isEqual(this.props.lessons, prevProps.lessons) &&
            this.props.lessons.length > 0 &&
            !this.state.selectedDay &&
            !this.props.selectedDate
        ) {
            // Select first event
            let lesson = this.props.lessons.find((l) => {
                let lDate = DateTime.fromISO(l.date);

                return Math.round(lDate.diffNow("days").days) >= 0;
            });

            if (!lesson) {
                lesson = this.props.lessons[0];
            }

            let date = DateTime.fromISO(lesson.date).toUTC();
            this.setState({
                selectedYear: date.year,
                selectedMonth: date.month,
                selectedDay: date.day,
            });
        }
    }

    _loadLessons() {
        this.props.loadLessons(this.begin, this.end);
    }

    _renderDay(now, day = null, selected) {
        const { selectedYear, selectedMonth, selectedDay } = this.state;
        const { lessons } = this.props;

        if (day === null) {
            return <div key={day} />;
        }

        let currentDay = DateTime.now().set({
            year: now.year,
            month: now.month,
            day: day,
        });

        let status = "empty";

        // Check if exists a lesson in this day
        if (
            lessons.some((lesson) => {
                let date = DateTime.fromISO(lesson.date).toUTC();

                if (date.hasSame(currentDay, "day")) {
                    return true;
                }
                return false;
            })
        ) {
            status = "lesson";
        }

        if (selected) {
            if (
                selectedDay === day &&
                selectedYear === now.year &&
                selectedMonth === now.month
            ) {
                status = "selected";
            }
        }

        let className =
            "h-40 border rounded-full disabled:cursor-default w-full items-center justify-center";

        if (status === "empty") {
            className += " border-blue";
        } else if (status === "lesson") {
            className += " border-blue bg-blue text-pink";
        } else if (status === "selected") {
            className += " border-white bg-white text-pink";
        }

        return (
            <div key={day}>
                <button
                    disabled={status !== "lesson"}
                    className={className + " hidden xl:flex"}
                    onClick={() => {
                        this.setState({
                            selectedYear: now.year,
                            selectedMonth: now.month,
                            selectedDay: day,
                        });
                    }}
                >
                    <p className="text-center font-judge text-21 font-bold">
                        {day}
                    </p>
                </button>
                <button
                    disabled={status !== "lesson"}
                    className={className + " xl:hidden flex"}
                    onClick={() => {
                        this.props.changeMobileClasse(true, {
                            selectedYear: now.year,
                            selectedMonth: now.month,
                            selectedDay: day,
                            selectedIndex: 0,
                        });
                    }}
                >
                    <p className="text-center font-judge text-21 font-bold">
                        {day}
                    </p>
                </button>
            </div>
        );
    }

    _renderDays(now, selected = true) {
        let firstDay = now.startOf("month");

        var days = [];
        for (let i = 1; i <= firstDay.weekday; i++) {
            days.push(null);
        }
        for (let i = 1; i <= now.daysInMonth; i++) {
            days.push(i);
        }

        return (
            <div className="grid grid-cols-7 pt-60 gap-6">
                {days.map((day) => this._renderDay(now, day, selected))}
            </div>
        );
    }

    _renderLessons(now) {
        const { selectedMonth, selectedYear, selectedDay } = this.state;
        const { lessons, locale } = this.props;

        if (
            !selectedDay ||
            selectedMonth !== now.month ||
            selectedYear !== now.year
        )
            return null;

        let currentDay = DateTime.now().set({
            year: selectedYear,
            month: selectedMonth,
            day: selectedDay,
        });

        let dayLessons = lessons.filter((lesson) => {
            let date = DateTime.fromISO(lesson.date).toUTC();

            if (date.hasSame(currentDay, "day")) {
                return true;
            }
            return false;
        });

        return (
            <div className="hidden xl:block flex-1 pl-30">
                <p className="text-30 font-judge font-bold">
                    {currentDay
                        .toLocaleString({ dateStyle: "short" })
                        .split("/")
                        .join(".")}
                </p>
                <p className="text-30 font-judge font-bold uppercase mb-30">
                    {currentDay.toLocaleString({ weekday: "long" })}
                </p>
                {dayLessons.map((lesson, index) => (
                    <Lesson
                        key={lesson.id}
                        lesson={lesson}
                        last={index === dayLessons.length - 1}
                    />
                ))}
            </div>
        );
    }

    _renderMonth(year, month) {
        let current = DateTime.local();
        let now = DateTime.local().set({ year: year, month: month });

        let monthText = now.toLocaleString({ month: "long" }).toUpperCase();
        if (current.year !== now.year) {
            monthText += " " + now.year;
        }

        return (
            <div className="flex border-l border-blue pl-16">
                <div className="w-380">
                    <p className="font-judge text-30 font-bold">{monthText}</p>
                    <div className="xl:hidden">
                        {this._renderDays(now, false)}
                    </div>
                    <div className="hidden xl:block">
                        {this._renderDays(now)}
                    </div>
                </div>
                {this._renderLessons(now)}
            </div>
        );
    }

    render() {
        const { selectedMonth, selectedYear, selectedDay } = this.state;

        let now = DateTime.local().set({ year: 2021, month: 9 });
        let end = DateTime.local();

        let initialSlide = 0;
        let months = [];
        let numMonths = Math.round(end.diff(now, "months").months) + 4;

        for (let i = 0; i < numMonths; i++) {
            months.push({ year: now.year, month: now.month });
            if (now.year === selectedYear && now.month === selectedMonth) {
                initialSlide = i;
            }
            now = now.plus({ month: 1 });
        }

        return (
            <div className="xl:px-50 pb-10 relative border-b border-blue overflow-hidden">
                <Swiper
                    navigation={{
                        prevEl: this._prevArrow,
                        nextEl: this._nextArrow,
                    }}
                    onBeforeInit={(swiper) => {
                        swiper.params.navigation.prevEl = this._prevArrow;
                        swiper.params.navigation.nextEl = this._nextArrow;
                    }}
                    simulateTouch={false}
                    initialSlide={initialSlide}
                    spaceBetween={16}
                    slidesPerView="auto"
                    onSlideChange={(swiper) => {
                        let changeDate = DateTime.local().set(
                            months[swiper.realIndex]
                        );
                        this.begin = changeDate
                            .startOf("month")
                            .minus({ month: 2 });
                        this.end = changeDate.endOf("month").plus({ month: 2 });
                        this._loadLessons();
                    }}
                >
                    {months.map((month, index) => {
                        let width = window.innerWidth > 1024 ? 1080 : 380;
                        if (
                            !selectedDay ||
                            selectedMonth !== month.month ||
                            selectedYear !== month.year
                        ) {
                            width = 380;
                        }
                        if (!selectedDay && index === initialSlide) {
                            width = window.innerWidth > 1024 ? 1080 : 380;
                        }

                        return (
                            <SwiperSlide key={index} style={{ width }}>
                                {this._renderMonth(month.year, month.month)}
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
                <div
                    className="absolute right-40 xl:right-auto xl:left-0 xl:top-250 top-0 cursor-pointer py-7 z-20"
                    ref={(arrow) => (this._prevArrow = arrow)}
                >
                    <IconArrow className="transform -rotate-90 w-26 h-20" />
                </div>

                <div className="absolute -right-50 -top-100 z-10">
                    <div className="div-blur bg-pink w-80 xl:w-140 h-380" />
                </div>

                <div
                    className="absolute right-0 xl:top-250 top-0 py-7 cursor-pointer z-10"
                    ref={(arrow) => (this._nextArrow = arrow)}
                >
                    <IconArrow className="transform rotate-90 w-26 h-20" />
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    locale: state.app.locale,
    lessons: getLessons(state),
});

const mapDispatchToProps = (dispatch) => {
    return bindActionCreators(
        {
            changeMobileClasse,
            loadLessons,
        },
        dispatch
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(Calendar);
